import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import {
  getTemplateFormData,
  TemplateFormDataResponse,
} from "../api/endpoints/fetchTemplates";
import { useAuth } from "../contexts/AuthContext";

export interface UseTemplateFormDataParams {
  templateId?: string | null;
  fileName: string;
}

export function useTemplateFormData(
  { templateId, fileName }: UseTemplateFormDataParams,
  options?: Omit<
    UseQueryOptions<
      TemplateFormDataResponse,
      Error,
      TemplateFormDataResponse,
      readonly unknown[]
    >,
    "queryKey" | "queryFn" | "enabled"
  >
): UseQueryResult<TemplateFormDataResponse, Error> {
  const { user } = useAuth();
  const emailId = user?.email || "";
  return useQuery<
    TemplateFormDataResponse,
    Error,
    TemplateFormDataResponse,
    readonly unknown[]
  >({
    queryKey: ["templateFormData", templateId] as const,
    queryFn: () => getTemplateFormData(String(templateId), emailId, fileName),
    enabled: Boolean(templateId),
    // Configure React Query to always call queryFn even when offline
    networkMode: 'always', // This ensures queryFn is called regardless of network status
    retry: false, // Disable automatic retries to let our custom offline logic handle it
    staleTime: 0, // Don't use stale data, always fetch fresh or from our IndexedDB
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (formerly cacheTime)
    ...options,
  });
}

